import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import type { PostType, TagComparator } from '../../graphqlEnums'
import { toUTCDayEnd, toUTCDayStart } from '../../hooks/toUTCDayBounds'

const AllChannelsPostInsightsDocument = graphql(/* GraphQL */ `
  query AllChannelsPostInsights($input: AllChannelsPostInsightsInput!) {
    allChannelsPostInsights(input: $input) {
      summary {
        type
        unit
        name
        description
        value
        difference
        differenceMode
      }
      channelInsights {
        channel {
          id
          name
          avatar
          service
        }
        metrics {
          type
          unit
          displayName
          description
          value
          difference
        }
      }
      channelTimeSeries {
        channel {
          id
          name
          service
        }
        firstChannelDataCollectedAt
        primary {
          followers {
            date
            value
            variation
          }
          reach {
            date
            value
            variation
          }
          views {
            date
            value
            variation
          }
          posts {
            date
            value
            variation
          }
        }
        comparison {
          followers {
            date
            value
          }
          reach {
            date
            value
          }
          views {
            date
            value
          }
          posts {
            date
            value
          }
        }
      }
    }
  }
`)

export type AllChannelsPostInsightsResult = ResultOf<
  typeof AllChannelsPostInsightsDocument
>

type AllChannelsPostInsightsData =
  AllChannelsPostInsightsResult['allChannelsPostInsights']

interface UseAllChannelsPostInsightsResult {
  data: Partial<AllChannelsPostInsightsData>
  loading: boolean
  error: ErrorLike | undefined
}

interface UseAllChannelsPostInsightsOptions {
  organizationId: string
  startDate: Date
  endDate: Date
  channelIds?: string[]
  tags?: TagComparator
  /** Post formats to include. An empty list means every format. */
  postTypes?: PostType[]
  comparisonStartDate?: Date
  comparisonEndDate?: Date
  /** Skip the query entirely (e.g. the actor can't view insights here). */
  skip?: boolean
}

export function useAllChannelsPostInsights({
  organizationId,
  startDate,
  endDate,
  channelIds,
  tags,
  postTypes,
  comparisonStartDate,
  comparisonEndDate,
  skip = false,
}: UseAllChannelsPostInsightsOptions): UseAllChannelsPostInsightsResult {
  const hasComparison = !!(comparisonStartDate && comparisonEndDate)
  // The query can only run once we have an org id and a date range. While those
  // prerequisites are still resolving the query is skipped, which Apollo reports
  // as `loading: false`. Treat that window as loading so consumers render their
  // loading state instead of briefly flashing an empty/no-data state on first
  // paint.
  const isWaitingForPrerequisites = !organizationId || !startDate || !endDate
  const { data, previousData, loading, error } = useQuery(
    AllChannelsPostInsightsDocument,
    {
      variables: {
        input: {
          organizationId,
          startDate: toUTCDayStart(startDate),
          endDate: toUTCDayEnd(endDate),
          ...(channelIds?.length ? { channelIds } : {}),
          ...(tags ? { tags } : {}),
          ...(postTypes?.length ? { postTypes } : {}),
          ...(hasComparison
            ? {
                comparisonStartDate: toUTCDayStart(comparisonStartDate),
                comparisonEndDate: toUTCDayEnd(comparisonEndDate),
              }
            : {}),
        },
      },
      fetchPolicy: 'cache-first',
      skip: skip || isWaitingForPrerequisites,
    },
  )

  // Use previous data while refetching to avoid layout jumps
  const effectiveData = data ?? previousData

  return {
    data: effectiveData?.allChannelsPostInsights ?? {},
    loading: loading || isWaitingForPrerequisites,
    error,
  }
}
